import React, { useState, useEffect, useMemo } from "react";
import { TransactionGroup } from "@/types/TransactionGroup";
import {
  FiPlus,
  FiMoreHorizontal,
  FiEdit2,
  FiTrash2,
  FiCopy,
  FiChevronLeft,
  FiChevronRight,
  FiSearch,
} from "react-icons/fi";
import { TransactionGroupService } from "@/services/TransactionGroupService";
import { useNavigate } from "react-router-dom";
import LoadingSpinner from "@/components/ui/LoadingSpinner";
import {
  Dropdown,
  DropdownTrigger,
  DropdownMenu,
  DropdownItem,
} from "@heroui/react";

const PAGE_SIZE = 25;

const TransactionGroups: React.FC = () => {
  const navigate = useNavigate();
  const [groups, setGroups] = useState<TransactionGroup[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [nameSearch, setNameSearch] = useState("");
  const [page, setPage] = useState(1);

  useEffect(() => {
    loadGroups();
  }, []);

  const loadGroups = async () => {
    setIsLoading(true);
    try {
      const fetchedGroups = await TransactionGroupService.getAll();
      setGroups(fetchedGroups);
    } catch (error) {
      console.error("Error loading transaction groups:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteGroup = async (id: string) => {
    if (!window.confirm("Are you sure you want to delete this template?")) {
      return;
    }
    setIsSaving(true);
    try {
      await TransactionGroupService.delete(id);
      setGroups(groups.filter((group) => group.id !== id));
    } catch (error) {
      console.error("Error deleting transaction group:", error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCloneGroup = async (group: TransactionGroup) => {
    setIsSaving(true);
    try {
      const cloned = await TransactionGroupService.create({
        name: `Copy of ${group.name}`,
        description: group.description,
        transactions: (group.transactions || []).map((t) => ({ ...t })),
        isDefault: false,
      });
      setGroups((prev) => [cloned, ...prev]);
    } catch (error) {
      console.error("Error cloning transaction group:", error);
      alert("Failed to clone template. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleViewGroup = (id: string) => {
    navigate(`/admin/transaction-groups/${id}`);
  };

  const formatDate = (date: Date) => {
    const d = new Date(date);
    const day = d.getDate();
    const month = d.toLocaleString("default", { month: "short" });
    const year = d.getFullYear();
    const hours = d.getHours();
    const minutes = d.getMinutes().toString().padStart(2, "0");
    const ampm = hours >= 12 ? "PM" : "AM";
    const displayHours = (hours % 12 || 12).toString().padStart(2, "0");
    return `${day} ${month} ${year} at ${displayHours}:${minutes} ${ampm}`;
  };

  const filteredGroups = useMemo(() => {
    const q = nameSearch.trim().toLowerCase();
    if (!q) return groups;
    return groups.filter((g) => g.name.toLowerCase().includes(q));
  }, [groups, nameSearch]);

  // Reset page when search changes
  useEffect(() => {
    setPage(1);
  }, [nameSearch]);

  const totalPages = Math.max(1, Math.ceil(filteredGroups.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const pageGroups = filteredGroups.slice(
    (safePage - 1) * PAGE_SIZE,
    safePage * PAGE_SIZE,
  );
  const rangeStart =
    filteredGroups.length === 0 ? 0 : (safePage - 1) * PAGE_SIZE + 1;
  const rangeEnd = Math.min(safePage * PAGE_SIZE, filteredGroups.length);

  if (isLoading) {
    return (
      <div className="bg-white rounded-2xl border border-gray-200 p-8">
        <LoadingSpinner
          size="large"
          message="Loading transaction templates..."
          className="py-8"
        />
      </div>
    );
  }

  return (
    <>
      {isSaving && (
        <div className="bg-white rounded-2xl border border-gray-200 p-8">
          <LoadingSpinner
            size="large"
            message="Saving changes..."
            className="py-8"
          />
        </div>
      )}
      <div className="container mx-auto px-4 py-8">
        <div className="bg-white rounded-lg shadow-sm">
          <div className="p-6">
            {/* Header */}
            <div className="flex justify-between items-start mb-4">
              <div>
                <h1 className="text-2xl font-bold text-gray-800 mb-2">
                  Base Transaction Templates
                </h1>
                <p className="text-gray-600">
                  Here's an overview of the base transaction templates created.
                </p>
              </div>
              <button
                onClick={() => navigate("/admin/transaction-groups/add")}
                className="bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 flex items-center gap-2"
                disabled={isSaving}
              >
                <FiPlus /> Add Template
              </button>
            </div>

            {/* Search */}
            <div className="mt-6 flex items-center gap-3">
              <div className="relative flex-1 max-w-sm">
                <FiSearch
                  size={16}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                />
                <input
                  type="text"
                  value={nameSearch}
                  onChange={(e) => setNameSearch(e.target.value)}
                  placeholder="Search by name"
                  className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-md text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <span className="text-sm text-gray-500 ml-auto">
                {filteredGroups.length}{" "}
                {filteredGroups.length === 1 ? "template" : "templates"}
              </span>
            </div>

            {/* Groups Table */}
            <table className="w-full mt-4">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-3 px-4 font-medium text-gray-700">
                    Name
                  </th>
                  <th className="text-left py-3 px-4 font-medium text-gray-700">
                    Description
                  </th>
                  <th className="text-left py-3 px-4 font-medium text-gray-700">
                    Transactions
                  </th>
                  <th className="text-left py-3 px-4 font-medium text-gray-700">
                    Last updated on
                  </th>
                  <th className="text-right py-3 px-4"></th>
                </tr>
              </thead>
              <tbody>
                {pageGroups.length === 0 ? (
                  <tr>
                    <td
                      colSpan={5}
                      className="text-center py-8 text-gray-500"
                    >
                      {filteredGroups.length === 0 && groups.length > 0
                        ? "No templates match your search."
                        : "No transaction templates found. Add your first template to get started."}
                    </td>
                  </tr>
                ) : (
                  pageGroups.map((group) => (
                    <tr
                      key={group.id}
                      className="border-b hover:bg-gray-50 cursor-pointer"
                      onClick={() => handleViewGroup(group.id)}
                    >
                      <td className="py-4 px-4 font-medium text-gray-900">
                        {group.name}
                      </td>
                      <td className="py-4 px-4 text-gray-600">
                        {group.description || "-"}
                      </td>
                      <td className="py-4 px-4 text-gray-600">
                        {group.transactions?.length || 0}
                      </td>
                      <td className="py-4 px-4 text-gray-600">
                        {formatDate(group.updatedAt || group.createdAt)}
                      </td>
                      <td className="py-4 px-4">
                        <div className="flex justify-end">
                          <Dropdown>
                            <DropdownTrigger>
                              <button
                                className="text-gray-600 hover:text-gray-800 p-1 rounded"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <FiMoreHorizontal size={20} />
                              </button>
                            </DropdownTrigger>
                            <DropdownMenu
                              aria-label="Actions"
                              onAction={(key) => {
                                if (key === "edit") {
                                  handleViewGroup(group.id);
                                } else if (key === "clone") {
                                  handleCloneGroup(group);
                                } else if (key === "delete") {
                                  handleDeleteGroup(group.id);
                                }
                              }}
                            >
                              <DropdownItem
                                key="edit"
                                startContent={<FiEdit2 size={16} />}
                              >
                                Edit
                              </DropdownItem>
                              <DropdownItem
                                key="clone"
                                startContent={<FiCopy size={16} />}
                              >
                                Clone
                              </DropdownItem>
                              <DropdownItem
                                key="delete"
                                className="text-danger"
                                color="danger"
                                startContent={<FiTrash2 size={16} />}
                              >
                                Delete
                              </DropdownItem>
                            </DropdownMenu>
                          </Dropdown>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>

            {/* Pagination */}
            {filteredGroups.length > PAGE_SIZE && (
              <div className="flex items-center justify-between mt-4">
                <span className="text-sm text-gray-600">
                  Showing {rangeStart}–{rangeEnd} of {filteredGroups.length}
                </span>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={safePage === 1}
                    className="p-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <FiChevronLeft size={16} />
                  </button>
                  <span className="text-sm text-gray-700">
                    Page {safePage} of {totalPages}
                  </span>
                  <button
                    onClick={() =>
                      setPage((p) => Math.min(totalPages, p + 1))
                    }
                    disabled={safePage === totalPages}
                    className="p-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <FiChevronRight size={16} />
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default TransactionGroups;
